import { useState, useRef, useEffect, forwardRef, type FC } from 'react';
import Icon, { type IconString } from '2-core-elements/Icon/Icon';

export interface SelectCustomOption {
    label: string;
    value: string | number;
}

export interface SelectCustomProps {
    prefixIcon?: IconString;
    icon?: IconString;
    className?: string;
    disabled?: boolean;
    placeholder?: string;
    id?: string;
    size?: 'sm' | 'md' | 'lg';
    options: SelectCustomOption[];
    onChange?: (newValue: SelectCustomOption | null) => void;
    value?: SelectCustomOption | null;
    freezePlaceholder?: boolean;
    hasError?: boolean;
    canUnsetValue?: boolean;
}

interface SelectCustomOptionProps {
    label: string;
    onClick: () => void;
    isActive?: boolean;
}

type ObjectCheck = SelectCustomOption | null | undefined;

function isValueEqual(a: ObjectCheck, b: ObjectCheck): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}

const SelectCustomOption: FC<SelectCustomOptionProps> = ({ label, onClick, isActive }) => {
    const optionClass = `select-custom-option ${isActive ? 'is-active' : ''}`;

    return (
        <button type="button" className={optionClass} onClick={onClick} aria-label={label}>
            {label}
        </button>
    );
};

const SelectCustom: FC<SelectCustomProps> = forwardRef<HTMLButtonElement, SelectCustomProps>((props, ref) => {
    const {
        prefixIcon,
        icon = 'chevron-down',
        className = '',
        disabled = false,
        placeholder = '',
        id,
        size = 'md',
        options,
        onChange,
        value,
        freezePlaceholder,
        hasError,
        canUnsetValue = true,
    } = props;

    const [isOpen, setIsOpen] = useState(false);
    const [values, setValues] = useState<SelectCustomOption | null | undefined>(undefined);
    const element = useRef<HTMLDivElement>(null);

    const onClick = (): void => {
        setIsOpen(!isOpen);
    };

    const handleOutsideClick = (e: Event): void => {
        if (element?.current?.contains(e.target as Node | null)) {
            // handle click inside
            return;
        }
        // handle click outside
        setIsOpen(false);
    };

    // add & remove listener on didMount and unMount
    useEffect(() => {
        if (value) {
            setValues(value);
        }

        window.addEventListener('mousedown', handleOutsideClick);

        // returned function will be called on component unmount
        return (): void => {
            window.removeEventListener('mousedown', handleOutsideClick);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // if not the same value, then set the value
        if (!isValueEqual(value, values)) {
            setValues(value);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => {
        if (onChange && values !== undefined && !isValueEqual(value, values)) {
            onChange(values);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values]);

    const classOutput = `select-custom-wrapper ${className} ${isOpen ? 'is-open' : ''}`;
    const activeClass = values ? 'is-active' : '';
    const selectClassOutput = `select-wrapper ${activeClass} ${hasError ? 'has-error' : ''} ${icon ? 'has-suffix-icon' : ''} ${prefixIcon ? 'has-prefix-icon' : ''} select-${size} ${className}`;

    return (
        <div ref={element} className={classOutput}>
            <div className={selectClassOutput}>
                {prefixIcon && <Icon className="prefix-icon" icon={prefixIcon} />}
                <button
                    type="button"
                    onClick={onClick}
                    className="select"
                    disabled={disabled}
                    id={id}
                    aria-controls={id}
                    aria-label={placeholder || 'Select an option'}
                    role="combobox"
                    aria-expanded={isOpen}
                    ref={ref}
                >
                    {(freezePlaceholder || (placeholder && !values)) && (
                        <span className="select-placeholder">{placeholder}</span>
                    )}
                    {!freezePlaceholder && values && (
                        <span className="select-text">{values.label}</span>
                    )}
                </button>
                {icon && <Icon className="suffix-icon" icon={icon} />}
            </div>
            {!!options?.length && (
                <div className="select-custom-content">
                    <div className="select-custom-content-container">
                        {options.map(({ label, value: optionValue }, i) => {
                            const key = `${label}-${value}-${i}`;
                            return (
                                <SelectCustomOption
                                    key={key}
                                    label={label}
                                    onClick={(): void => {
                                        if (values && values.value === optionValue && canUnsetValue) {
                                            setValues(null);
                                        } else {
                                            setValues({ label, value: optionValue });
                                        }
                                        setIsOpen(false);
                                    }}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
});

SelectCustom.displayName = 'SelectCustom';

export default SelectCustom;
